import { useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Collapse,
  Popover,
  Paper
} from '@mui/material';
import {
  Menu as MenuIcon,
  ViewModule as ViewModuleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CloudQueue as CloudQueueIcon,
  AccountTree as AccountTreeIcon,
  CompareArrows as CompareArrowsIcon,
  Shield as ShieldIcon,
  Storage as StorageIcon,
  ModelTraining as ModelTrainingIcon,
  Dashboard as DashboardIcon,
  Science as ScienceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Dataset as DatasetIcon
} from '@mui/icons-material';
import { Header } from './Header';

interface LayoutProps {
  children: ReactNode;
}

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 64;

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [promptLabExpanded, setPromptLabExpanded] = useState(false);
  const [promptLabAnchor, setPromptLabAnchor] = useState<null | HTMLElement>(null);
  const [evaluationExpanded, setEvaluationExpanded] = useState(false);
  const [evaluationAnchor, setEvaluationAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/') return location.pathname === '/' || location.pathname === '/catalog';
    if (path === '/catalog') return location.pathname === '/catalog' || location.pathname.startsWith('/model/') || location.pathname === '/';
    if (path === '/deployments') return location.pathname === '/deployments' || location.pathname.startsWith('/deployment/');
    if (path === '/compare') return location.pathname === '/compare';
    if (path === '/model-route-config') return location.pathname === '/model-route-config' || location.pathname.startsWith('/model-route-config/');
    if (path === '/settings') return location.pathname === '/settings' || location.pathname.startsWith('/settings/');
    if (path === '/prompt-lab/usage-categories') return location.pathname === '/prompt-lab/usage-categories';
    if (path === '/prompt-lab') return location.pathname === '/prompt-lab' || location.pathname.startsWith('/prompt-lab/add') || location.pathname.startsWith('/prompt-lab/edit');
    if (path === '/rag') return location.pathname === '/rag' || location.pathname.startsWith('/rag/');
    if (path === '/fine-tuning') return location.pathname === '/fine-tuning' || location.pathname.startsWith('/fine-tuning/');
    if (path === '/evaluation/datasets') return location.pathname === '/evaluation/datasets';
    if (path === '/evaluation') return location.pathname === '/evaluation';
    return false;
  };

  const menuItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { label: 'Model Catalog', icon: <ViewModuleIcon />, path: '/catalog' },
    { label: 'Deployments', icon: <CloudQueueIcon />, path: '/deployments' },
    // { label: 'Routing Config', icon: <AccountTreeIcon />, path: '/model-route-config' },
    // { label: 'Compare Models', icon: <CompareArrowsIcon />, path: '/compare' },
    // { label: 'Guardrails', icon: <ShieldIcon />, path: '/guardrails' },
    { label: 'RAG', icon: <StorageIcon />, path: '/rag' },
    // { label: 'Fine-Tuning', icon: <ModelTrainingIcon />, path: '/fine-tuning' },
  ];

  const promptLabSubItems = [
    { label: 'Usage Category', icon: <CategoryIcon />, path: '/prompt-lab/usage-categories' },
    { label: 'Prompt Template', icon: <DescriptionIcon />, path: '/prompt-lab' },
  ];

  const evaluationSubItems = [
    { label: 'Datasets', icon: <DatasetIcon />, path: '/evaluation/datasets' },
    { label: 'Evaluation', icon: <AssessmentIcon />, path: '/evaluation' },
  ];

  const drawer = (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#ffffff'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          px: collapsed ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          height: { xs: 64, sm: 70 }
        }}
      >
        {collapsed ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ViewModuleIcon sx={{ fontSize: 20, color: 'white' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ViewModuleIcon sx={{ fontSize: 20, color: 'white' }} />
            </Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#0f172a'
              }}
            >
              AI Platform
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ flex: 1, p: 2 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    px: collapsed ? 2 : 2,
                    py: 1.5,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    bgcolor: active ? '#eff6ff' : 'transparent',
                    '&:hover': {
                      bgcolor: active ? '#dbeafe' : '#f8fafc'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      color: active ? '#2563eb' : '#64748b',
                      '& svg': { fontSize: 22 }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '14px',
                          fontWeight: active ? 600 : 500,
                          color: active ? '#0f172a' : '#475569'
                        }
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          {/* Prompt Lab Accordion */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => {
                if (collapsed) {
                  setPromptLabAnchor(e.currentTarget);
                } else {
                  setPromptLabExpanded(!promptLabExpanded);
                }
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                px: collapsed ? 2 : 2,
                py: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                bgcolor: location.pathname.startsWith('/prompt-lab') ? '#eff6ff' : 'transparent',
                '&:hover': {
                  bgcolor: location.pathname.startsWith('/prompt-lab') ? '#dbeafe' : '#f8fafc'
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  color: location.pathname.startsWith('/prompt-lab') ? '#2563eb' : '#64748b',
                  '& svg': { fontSize: 22 }
                }}
              >
                <ScienceIcon />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary="Prompt Lab"
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '14px',
                        fontWeight: location.pathname.startsWith('/prompt-lab') ? 600 : 500,
                        color: location.pathname.startsWith('/prompt-lab') ? '#0f172a' : '#475569'
                      }
                    }}
                  />
                  {promptLabExpanded ? <ExpandLessIcon sx={{ color: '#64748b' }} /> : <ExpandMoreIcon sx={{ color: '#64748b' }} />}
                </>
              )}
            </ListItemButton>
          </ListItem>

          {/* Prompt Lab Sub-items */}
          {!collapsed && (
            <Collapse in={promptLabExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {promptLabSubItems.map((subItem) => {
                  const active = isActive(subItem.path);
                  
                  return (
                    <ListItem key={subItem.path} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigate(subItem.path)}
                        sx={{
                          borderRadius: 1.5,
                          mb: 0.5,
                          pl: 6,
                          pr: 2,
                          py: 1.25,
                          bgcolor: active ? '#eff6ff' : 'transparent',
                          '&:hover': {
                            bgcolor: active ? '#dbeafe' : '#f8fafc'
                          }
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: active ? '#2563eb' : '#64748b',
                            '& svg': { fontSize: 20 }
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '13px',
                              fontWeight: active ? 600 : 500,
                              color: active ? '#0f172a' : '#475569'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          )}

          {/* Evaluation Accordion */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => {
                if (collapsed) {
                  setEvaluationAnchor(e.currentTarget);
                } else {
                  setEvaluationExpanded(!evaluationExpanded);
                }
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                px: collapsed ? 2 : 2,
                py: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                bgcolor: location.pathname.startsWith('/evaluation') ? '#eff6ff' : 'transparent',
                '&:hover': {
                  bgcolor: location.pathname.startsWith('/evaluation') ? '#dbeafe' : '#f8fafc'
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  color: location.pathname.startsWith('/evaluation') ? '#2563eb' : '#64748b',
                  '& svg': { fontSize: 22 }
                }}
              >
                <AssessmentIcon />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary="Evaluation"
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '14px',
                        fontWeight: location.pathname.startsWith('/evaluation') ? 600 : 500,
                        color: location.pathname.startsWith('/evaluation') ? '#0f172a' : '#475569'
                      }
                    }}
                  />
                  {evaluationExpanded ? <ExpandLessIcon sx={{ color: '#64748b' }} /> : <ExpandMoreIcon sx={{ color: '#64748b' }} />}
                </>
              )}
            </ListItemButton>
          </ListItem>

          {/* Evaluation Sub-items */}
          {!collapsed && (
            <Collapse in={evaluationExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {evaluationSubItems.map((subItem) => {
                  const active = isActive(subItem.path);
                  
                  return (
                    <ListItem key={subItem.path} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigate(subItem.path)}
                        sx={{
                          borderRadius: 1.5,
                          mb: 0.5,
                          pl: 6,
                          pr: 2,
                          py: 1.25,
                          bgcolor: active ? '#eff6ff' : 'transparent',
                          '&:hover': {
                            bgcolor: active ? '#dbeafe' : '#f8fafc'
                          }
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: active ? '#2563eb' : '#64748b',
                            '& svg': { fontSize: 20 }
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '13px',
                              fontWeight: active ? 600 : 500,
                              color: active ? '#0f172a' : '#475569'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          )}
        </List>
      </Box>

      {/* Popover for Prompt Lab when collapsed */}
      <Popover
        open={Boolean(promptLabAnchor)}
        anchorEl={promptLabAnchor}
        onClose={() => setPromptLabAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            ml: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 200
          }
        }}
      >
        <Paper sx={{ p: 1 }}>
          <List sx={{ p: 0 }}>
            {promptLabSubItems.map((subItem) => {
              const active = isActive(subItem.path);
              
              return (
                <ListItem key={subItem.path} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      handleNavigate(subItem.path);
                      setPromptLabAnchor(null);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.5,
                      px: 2,
                      py: 1.25,
                      bgcolor: active ? '#eff6ff' : 'transparent',
                      '&:hover': {
                        bgcolor: active ? '#dbeafe' : '#f8fafc'
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? '#2563eb' : '#64748b',
                        '& svg': { fontSize: 20 }
                      }}
                    >
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={subItem.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '13px',
                          fontWeight: active ? 600 : 500,
                          color: active ? '#0f172a' : '#475569'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Popover>

      {/* Popover for Evaluation when collapsed */}
      <Popover
        open={Boolean(evaluationAnchor)}
        anchorEl={evaluationAnchor}
        onClose={() => setEvaluationAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            ml: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 200
          }
        }}
      >
        <Paper sx={{ p: 1 }}>
          <List sx={{ p: 0 }}>
            {evaluationSubItems.map((subItem) => {
              const active = isActive(subItem.path);
              
              return (
                <ListItem key={subItem.path} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      handleNavigate(subItem.path);
                      setEvaluationAnchor(null);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.5,
                      px: 2,
                      py: 1.25,
                      bgcolor: active ? '#eff6ff' : 'transparent',
                      '&:hover': {
                        bgcolor: active ? '#dbeafe' : '#f8fafc'
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? '#2563eb' : '#64748b',
                        '& svg': { fontSize: 20 }
                      }}
                    >
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={subItem.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '13px',
                          fontWeight: active ? 600 : 500,
                          color: active ? '#0f172a' : '#475569'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Popover>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8fafc' }}>
      {/* Mobile App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { lg: 'none' },
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: '#0f172a' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <ViewModuleIcon sx={{ fontSize: 18, color: 'white' }} />
          </Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>
            AI Platform
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: { lg: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH },
          flexShrink: { lg: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: '#ffffff',
              borderRight: '1px solid #e2e8f0'
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              bgcolor: '#ffffff',
              borderRight: '1px solid #e2e8f0',
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          mt: { xs: 8, lg: 0 },
        }}
      >
        <Header collapsed={collapsed} onToggleCollapse={() => setCollapsed(!collapsed)} />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
